<template>
  <div class="container">
    <a-spin v-if="!isShowContent" :spinning="true" :indicator="indicator" tip="Loading..." class="theater_loading" />
    <template v-else>
      <div v-if="theaterList.length > 0" class="theater_wrapper">
        <div
          class="theater_card"
          v-for="(item, index) in theaterList"
          :key="index"
          @click="handleInitVideoView(theaterList, index)"
        >
          <div class="video_container">
            <video
              :poster="posterSrc(item)"
              oncontextmenu="return false;"
              :id="'videoPlayer' + index"
              class="theater_video"
              :src="playSrc(item)"
              muted
              @mouseenter="handleMouseEnter($event)"
              @mouseleave="handleMouseLeave($event)"
            ></video>
            <div class="play_overlay">
              <div class="play_button">
                <div class="play_triangle"></div>
              </div>
            </div>
            <span v-show="videoDurationList[index]" class="video_duration">
              {{ formatToMinutes(videoDurationList[index]) }}
            </span>
          </div>
          <div class="card_content">
            <h3 class="theater_title">{{ item.title }}</h3>
            <p class="theater_description">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <div v-if="theaterList.length == 0 && isShowContent" class="theater_wrapper theater_wrapper_no_data">
        <img class="no_data_img" src="@/assets/images/workplace_theater_no_data.svg" alt="" />
        <h1 class="main_title">Workplace Theater</h1>
        <p class="subtitle">
          Workplace Theater is a JoyJobs storytelling space where everyday workplace moments come to life.
        </p>
        <p>
          Through short, simple, and sometimes humorous scenarios, we share the small stories that often happen at work
          — misunderstandings, growth moments, learning curves, and quiet wins. These stories are designed to help job
          seekers understand what positive work ethic looks like in real life: showing up, communicating, taking
          responsibility, and learning from mistakes.
        </p>
        <p>
          This series is especially created to make workplace expectations easier to understand, remember, and practice
          — without lectures or pressure.
        </p>
        <p class="sub_title">Official launch planned for 2027</p>
        <p>We're currently in early development. Stay tuned — more stories are coming soon.</p>
        <a-divider />
        <p class="sub_title">💡Have a real workplace story to share?</p>
        <p>
          We're seeking authentic, everyday experiences from professionals that can provide insights, inspire teams, and
          drive growth. If you'd like to contribute a story or case study, please reach out to
        </p>
        <a href="mailto:hello@joylanders.com" class="email_button">
          <span>📩 hello@joylanders.com</span>
        </a>
      </div>
      <VideoViewer ref="VideoViewerRef" />
    </template>
  </div>
</template>

<script setup>
import { LoadingOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { onMounted, ref, h } from 'vue';

import { fetchTheaterList } from '@/api/theater';
import VideoViewer from '@/components/VideoViewer.vue';
import { playSrc, posterSrc } from '@/utils/vod.js';

const indicator = h(LoadingOutlined, {
  style: {
    fontSize: '24px',
    color: '#05644f',
  },
  spin: true,
});

const theaterList = ref([]);
const isShowContent = ref(false);

const getTheaterList = () => {
  fetchTheaterList().then((res) => {
    isShowContent.value = true;
    if (res.code === 0) {
      theaterList.value = res.data;
    } else {
      message.error(res.msg);
    }
  });
};

const VideoViewerRef = ref(null);
const handleInitVideoView = (videoList, index) => {
  VideoViewerRef.value.openViewer(videoList, index);
};
const handleMouseEnter = (event) => {
  const video = event.target;
  video.muted = true;
  const playPromise = video.play();
  if (playPromise !== undefined) {
    playPromise.catch((error) => {
      console.warn('Video play interrupted:', error.message);
    });
  }
};

const handleMouseLeave = (event) => {
  const video = event.target;
  video.pause();
};

const videoDurationList = ref([]);
const getVideoDuration = (index) => {
  const video = document.getElementById('videoPlayer' + index);
  if (video) {
    videoDurationList.value[index] = video.duration;
  }
};
const formatToMinutes = (seconds) => {
  if (!seconds) return '0:00';
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

onMounted(() => {
  getTheaterList();
});
</script>

<style lang="less" scoped>
.container {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px 100px;
  color: #05644f;

  .theater_loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    .ant-spin-text {
      color: #05644f;
      margin-top: 16px;
      font-size: 14px;
    }
  }

  .theater_wrapper {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-bottom: 100px;

    .theater_card {
      background: #ffffff;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05);
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      border: 1px solid #f3f4f6;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
        border-color: rgba(5, 100, 79, 0.15);

        .play_overlay {
          opacity: 1;
          background: rgba(0, 0, 0, 0.4);
        }

        .play_button {
          transform: scale(1.1);
        }
      }

      .video_container {
        position: relative;
        aspect-ratio: 16 / 9;
        background: #1a1a1a;

        .theater_video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .play_overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.2);
          opacity: 0;
          transition: all 0.3s ease;

          .play_button {
            width: 64px;
            height: 64px;
            background: rgba(255, 255, 255, 0.95);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            transition: transform 0.2s ease;

            .play_triangle {
              width: 0;
              height: 0;
              border-top: 12px solid transparent;
              border-bottom: 12px solid transparent;
              border-left: 20px solid #1a1a1a;
              margin-left: 4px;
            }
          }
        }

        .video_duration {
          position: absolute;
          bottom: 12px;
          right: 12px;
          background: rgba(0, 0, 0, 0.5);
          color: #ffffff;
          font-size: 12px;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 4px;
        }
      }

      .card_content {
        padding: 20px;

        .theater_title {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 8px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .theater_description {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }
  }

  .theater_wrapper_no_data {
    display: block;
    max-width: 720px;
    margin: 0 auto;
    text-align: center;
    line-height: 1.7;
    padding: 0 16px;

    .no_data_img {
      max-width: 400px;
      width: 100%;
      height: auto;
      margin: 0 auto 24px;
    }

    .main_title {
      font-size: 32px;
      font-weight: bold;
      color: #05644f;
      margin: 0 0 12px;
    }

    .subtitle {
      font-size: 15px;
      font-weight: 400;
      color: #6b7280;
      margin-bottom: 32px;
      line-height: 1.6;
    }

    > p {
      margin-bottom: 20px;
      color: #4b5563;
      font-size: 15px;
      font-weight: 400;
      line-height: 1.8;
      max-width: 640px;
      margin-left: auto;
      margin-right: auto;
    }

    .sub_title {
      font-weight: 600;
      font-size: 15px;
      color: #1a1a1a;
      margin-top: 32px;
      margin-bottom: 12px;
      letter-spacing: -0.2px;
    }

    .ant-divider {
      color: #05644f;
      margin: 32px auto;
      max-width: 200px;
      opacity: 0.5;
    }

    .email_button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: #05644f;
      font-size: 15px;
      font-weight: 500;
      text-decoration: none;
      padding: 14px 32px;
      background: #ffffff;
      border-radius: 100px;
      border: 1px solid rgba(5, 100, 79, 0.25);
      transition: all 0.2s ease;
      margin-top: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

      &:hover {
        background: rgba(5, 100, 79, 0.06);
        border-color: rgba(5, 100, 79, 0.4);
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(5, 100, 79, 0.12);
      }
    }
  }

  @media (max-width: 1024px) {
    .theater_wrapper {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }
  }

  @media (max-width: 640px) {
    padding: 80px 16px 60px;

    .title {
      font-size: 20px;
    }

    .theater_wrapper {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }
}
</style>

<style lang="less">
.theater_loading {
  .ant-spin-text {
    color: #05644f !important;
  }
}
</style>
